import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { fetchCpmGraph } from '@/api/product-mapping';
import type { GraphNode } from '@/api/product-mapping/types';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { ensurePermissions, usePermissions } from '@/hooks/usePermissions';
import { queryClient } from '@/lib/utils/queryClient';
import { AccessRemoved } from '../../../-components/AccessRemoved';
import { CpmCollaborationDetailsSheet } from '../../../-components/CpmCollaborationDetailsSheet';
import { CpmDetailHeader } from '../../../-components/CpmDetailHeader';
import { CpmDiagram } from '../../../-components/CpmDiagram';
import { CpmReviewSheet } from '../../../-components/CpmReviewSheet';
import { useCpmGraph } from '../../../-hooks/useCpmGraph';
import { getCpmEditLock } from '../../../-utils/cpmEditLock';

export const Route = createFileRoute(
  '/(client)/product-mapping/(pages)/(details)/$cpmId/',
)({
  beforeLoad: async () => {
    const perms = await ensurePermissions();
    if (perms['product-mapping'].canRead) return;
    throw redirect({ to: '/dashboard' });
  },
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: ['cpm', 'graph', params.cpmId],
      queryFn: () => fetchCpmGraph(params.cpmId),
    }),
  component: CpmGraphPage,
});

function CpmGraphPage() {
  const { cpmId } = Route.useParams();
  const { 'product-mapping': productMapping } = usePermissions();
  const { data, isLoading, error, refetch } = useCpmGraph(cpmId);
  const [viewTaskOpen, setViewTaskOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<string | undefined>(
    undefined,
  );

  const graph = data?.data;

  if (!isLoading && error) {
    const status = (error as { status?: number }).status;
    if (status === 404 || status === 403) return <AccessRemoved />;
    return <QueryErrorState error={error} onRetry={() => void refetch()} />;
  }

  if (!isLoading && !graph) return <AccessRemoved />;

  const isOwner = graph?.collaborator?.collaborationType === 'OWNER';
  const isCollaborator = !isOwner;
  const { isEditLocked, isFacilityEditLocked } = getCpmEditLock(
    graph,
    isOwner,
    productMapping.canEdit,
  );

  const openReviewList = () => {
    setReviewTarget(undefined);
    setReviewOpen(true);
  };

  const openReviewForNode = (node: GraphNode) => {
    const rawNode = graph?.layers
      ?.flatMap(l => l.nodes)
      .find(n => n.id === node.id);
    const collaboratorId = rawNode?.collaborator?.id;
    if (collaboratorId) {
      setReviewTarget(collaboratorId);
      setReviewOpen(true);
    }
  };

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
      {graph && (
        <CpmDetailHeader
          graph={graph}
          collaboratorId={cpmId}
          {...(isCollaborator
            ? { onViewTask: () => setViewTaskOpen(true) }
            : {})}
          {...(graph.stats.reviewCount > 0
            ? { onReviewBadgeClick: openReviewList }
            : {})}
        />
      )}
      <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
        <CpmDiagram
          collaboratorId={graph?.collaborator?.id ?? cpmId}
          currentCollaboratorId={graph?.collaborator?.id ?? cpmId}
          graph={
            graph ?? {
              name: '',
              stats: {
                acceptedCount: 0,
                approvedCount: 0,
                invitedCount: 0,
                requestChangesCount: 0,
                reviewCount: 0,
              },
            }
          }
          isLoading={isLoading}
          error={error}
          onRefetch={() => void refetch()}
          isEditLocked={isEditLocked}
          isFacilityEditLocked={isFacilityEditLocked}
          onNodeStatusIconClick={openReviewForNode}
          {...(graph?.id !== undefined
            ? { supplyChainId: graph.id }
            : graph?.supplyChainId !== undefined
              ? { supplyChainId: graph.supplyChainId }
              : {})}
        />
      </div>

      {isCollaborator && graph && (
        <CpmCollaborationDetailsSheet
          open={viewTaskOpen}
          onOpenChange={setViewTaskOpen}
          collaboratorId={graph.collaborator?.id ?? cpmId}
          collaborationStatus={graph.collaborator?.collaborationStatus ?? null}
        />
      )}

      <CpmReviewSheet
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        reviewerCollaboratorId={cpmId}
        {...(reviewTarget !== undefined
          ? { preselectedCollaboratorId: reviewTarget }
          : {})}
      />
    </div>
  );
}
